<script lang="ts">
	// UC-65: a shop's drivers (D28), a task under the shop beside its delivery
	// areas (D5) and its tables (D23), laid out as Tables.svelte is.
	//
	// A driver is only a name; he signs in nowhere. The list comes from the
	// server in name order and is read again after every change rather than
	// sorted here. A name is changed in place; a new driver is a blank row at
	// the end. The rules are the server's: a name of 1 to MaxDriverNameLength
	// characters once trimmed, one line, unique in the shop whatever its case,
	// spacing or Arabic letter form, removed drivers included, and each
	// refusal shows in place, in the owner's words.
	//
	// Removing a driver is undone, not confirmed: he leaves the list at once
	// and the server hears of it when the Undo runs out (owner/notices.svelte.ts).
	// The tills stop offering him then, and his name stays taken. Until then
	// every read leaves him out, on this panel and on one opened after the
	// owner left it (owner/driverRemovals.svelte.ts), and an Undo puts him
	// back once. A removal the server refuses is said under the list.

	import Button from '../components/Button.svelte';
	import Card from '../components/Card.svelte';
	import Field from '../components/Field.svelte';
	import Loading from '../components/Loading.svelte';
	import { api } from '../api/client';
	import { fill, message, type Language } from '../i18n/messages';
	import { flash, removeWithUndo } from '../owner/notices.svelte';
	import {
		clearRefusedRemoval,
		holdRemoval,
		pendingRemovals,
		refusedRemoval,
		removalsSettled,
		settleRemoval,
	} from '../owner/driverRemovals.svelte';
	import type { components } from '../api/types';

	type Driver = components['schemas']['DriverBody'];

	interface Props {
		language: Language;
		shopId: string;
	}

	let { language, shopId }: Props = $props();

	// The server's list as last read, before the removals that wait for
	// their Undo are taken out of it.
	let read = $state<Driver[]>([]);
	const drivers = $derived(read.filter((d) => !pendingRemovals().includes(d.id)));
	let loaded = $state(false);
	// A read that failed, which says so rather than show an empty shop.
	let unread = $state(false);
	let edit = $state<{ id: string; name: string; error: string } | null>(null);
	let busy = $state(false);
	let failure = $state('');

	// The list follows the shop on screen. A shop's first read shows the wait;
	// a read after a change keeps the list in place until it answers. Reads
	// can overlap, and only the latest one asked replaces the list.
	let readFor = '';
	let asked = 0;
	$effect(() => {
		if (shopId === readFor) return;
		readFor = shopId;
		edit = null;
		failure = '';
		loaded = false;
		void load(shopId);
	});

	// A removal that settled, sent or undone, perhaps while another task was
	// on screen: read the list again, which holds the server's answer.
	let seenSettled = removalsSettled();
	$effect(() => {
		const now = removalsSettled();
		if (now === seenSettled) return;
		seenSettled = now;
		if (readFor) void load(readFor);
	});

	async function load(shop: string) {
		const ask = ++asked;
		let answer: Driver[] | null = null;
		try {
			const { data } = await api.GET('/api/owner/shops/{shopId}/drivers', {
				params: { path: { shopId: shop } },
			});
			answer = data ? (data.drivers ?? []) : null;
		} catch {
			// The read failed: said below, with Retry.
		}
		if (shop !== readFor || ask !== asked) return;
		unread = answer === null;
		read = answer ?? [];
		loaded = true;
	}

	function startNew() {
		failure = '';
		clearRefusedRemoval();
		edit = { id: 'new', name: '', error: '' };
	}

	async function save() {
		const current = edit;
		if (!current || !current.name.trim() || busy) return;
		busy = true;
		failure = '';
		clearRefusedRemoval();
		const body = { name: current.name.trim() };
		try {
			const result =
				current.id === 'new'
					? await api.POST('/api/owner/shops/{shopId}/drivers', {
							params: { path: { shopId } },
							body,
						})
					: await api.PATCH('/api/owner/shops/{shopId}/drivers/{driverId}', {
							params: { path: { shopId, driverId: current.id } },
							body,
						});
			if (result.error !== undefined || !result.data) {
				const code = result.error?.code ?? '';
				if (code === 'DRIVER_NAME_TAKEN') {
					current.error = message(language, 'driverNameTaken');
				} else if (code === 'VALIDATION_ERROR') {
					current.error = message(language, 'driverNameBounds');
				} else {
					// Said in the owner's words, never the server's English: a
					// driver a till's manager removed meanwhile is gone.
					failure = message(language, code === 'NOT_FOUND' ? 'driverGone' : 'driverNotSaved');
					edit = null;
					await load(shopId);
				}
				return;
			}
			edit = null;
			flash(message(language, 'saved'));
			await load(shopId);
		} catch {
			failure = message(language, 'loadFailed');
		} finally {
			busy = false;
		}
	}

	function remove(driver: Driver) {
		const shop = shopId;
		failure = '';
		holdRemoval(driver.id);
		removeWithUndo(
			fill(message(language, 'driversRemoved'), driver.name),
			async () => {
				// The removal reaches the server only now, perhaps after the
				// owner left this task: a refusal is kept by name, so whichever
				// Drivers panel is on screen says it. A driver already gone
				// (NOT_FOUND, a till's manager removed him) is removed all the same.
				let refused = '';
				try {
					const { error } = await api.POST('/api/owner/shops/{shopId}/drivers/{driverId}/remove', {
						params: { path: { shopId: shop, driverId: driver.id } },
					});
					if (error !== undefined && error.code !== 'NOT_FOUND') refused = driver.name;
				} catch {
					refused = driver.name;
				}
				settleRemoval(driver.id, refused);
			},
			() => settleRemoval(driver.id),
		);
	}
</script>

<Card title={message(language, 'driversTitle')} testId="drivers-{shopId}">
	<div class="rules">
		<p>{message(language, 'driversRuleWhat')}</p>
		<p>{message(language, 'driversRuleKeep')}</p>
		<p>{message(language, 'driversRuleTill')}</p>
	</div>
	{#if !loaded}
		<div class="wait">
			<Loading
				label={message(language, 'driversLoading')}
				caption={message(language, 'driversLoading')}
			/>
		</div>
	{:else}
		{#if unread}
			<div class="unread" role="alert" data-testid="drivers-unread">
				<p class="empty">{message(language, 'driversLoadFailed')}</p>
				<Button
					testId="drivers-retry"
					label={message(language, 'retry')}
					tone="secondary"
					size="sm"
					onPress={() => void load(shopId)}
				/>
			</div>
		{:else if drivers.length === 0 && edit?.id !== 'new'}
			<p class="empty" data-testid="drivers-empty">{message(language, 'driversEmpty')}</p>
		{/if}
		{#each [...drivers, ...(edit?.id === 'new' ? [{ id: 'new', name: '' }] : [])] as driver (driver.id)}
			<div class="row" data-testid="driver-{driver.id}">
				{#if edit && edit.id === driver.id}
					<div class="name-field">
						<Field
							testId="driver-name"
							ariaLabel={message(language, 'driversName')}
							placeholder={message(language, 'driversNameHint')}
							value={edit.name}
							maxLength={200}
							error={edit.error || undefined}
							errorTestId="driver-name-error"
							onInput={(v) => {
								if (edit) {
									edit.name = v;
									edit.error = '';
								}
							}}
						/>
					</div>
					<Button
						testId="driver-save"
						label={message(language, 'save')}
						size="sm"
						state={busy ? 'pending' : 'idle'}
						pendingLabel={message(language, 'saving')}
						disabled={!edit.name.trim()}
						onPress={() => void save()}
					/>
					<Button
						testId="driver-cancel"
						label={message(language, 'cancel')}
						tone="text-quiet"
						size="sm"
						disabled={busy}
						onPress={() => (edit = null)}
					/>
				{:else}
					<span class="label"><bdi>{driver.name}</bdi></span>
					<Button
						testId="driver-edit-{driver.id}"
						label={message(language, 'edit')}
						tone="text"
						size="sm"
						disabled={edit !== null}
						onPress={() => (edit = { id: driver.id, name: driver.name, error: '' })}
					/>
					<Button
						testId="driver-remove-{driver.id}"
						label={message(language, 'remove')}
						tone="text-quiet"
						size="sm"
						disabled={edit !== null}
						onPress={() => remove(driver)}
					/>
				{/if}
			</div>
		{/each}
	{/if}
	<div class="add">
		{#if failure}
			<p class="said" role="alert" data-testid="drivers-failure">{failure}</p>
		{/if}
		{#if refusedRemoval()}
			{@const [before, after] = message(language, 'driversRemoveRefused').split('%1')}
			<p class="said" role="alert" data-testid="drivers-remove-refused">
				{before}<bdi>{refusedRemoval()}</bdi>{after}
			</p>
		{/if}
		<Button
			testId="driver-add"
			icon="plus"
			label={message(language, 'driversAdd')}
			tone="secondary"
			size="sm"
			disabled={edit !== null || !loaded || unread}
			onPress={startNew}
		/>
	</div>
</Card>

<style>
	.rules {
		display: flex;
		flex-direction: column;
		gap: var(--space-2);
		padding: var(--row-pad);
		font-size: var(--text-2xs);
		line-height: var(--leading-snug);
		color: var(--text-muted);
		border-block-end: var(--border-width) solid var(--border-inset);
		text-wrap: pretty;
	}

	.rules p {
		margin: 0;
	}

	.wait {
		display: flex;
		justify-content: center;
		padding-block: var(--space-9);
	}

	.empty {
		margin: 0;
		padding: var(--card-pad);
		font-size: var(--text-sm);
		line-height: var(--leading-snug);
		color: var(--text-body);
	}

	.unread {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: var(--space-3);
		padding: 0 var(--card-pad) var(--card-pad);
	}

	.unread .empty {
		padding: var(--card-pad) 0 0;
	}

	.row {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: var(--space-5);
		padding: var(--row-pad);
		border-block-end: var(--border-width) solid var(--border-inset);
	}

	.label {
		flex: 1;
		min-inline-size: 0;
		font-size: var(--text-sm);
		font-weight: 600;
		overflow-wrap: anywhere;
	}

	.name-field {
		flex: 1 1 160px;
		min-inline-size: 0;
	}

	.add {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: var(--space-3);
		padding: var(--row-pad);
	}

	.said {
		margin: 0;
		font-size: var(--text-2xs);
		font-weight: 600;
		color: var(--ink-flag-deep);
	}
</style>
